import React, { useState } from "react";
import { Link } from "react-router-dom";

const LANGS = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
];

const CONTENT = {
    en: {
        kicker: "A Simple Way",
        title: "How to Pray",
        lead: "There is no formula. There is only the turning of the heart. But here are some quiet doorways in — practices the Church has taught for centuries.",
        sections: [
            {
                heading: "Begin with silence",
                body: "Before words, come stillness. Sit somewhere quiet. Breathe. Make the Sign of the Cross — slowly. Recall that you are in the presence of the living God. This alone is prayer.",
            },
            {
                heading: "Speak simply",
                body: "You do not need eloquence. Speak as you would to a father, a brother, a friend. Tell Him what is in your heart — the joy, the grief, the shame, the gratitude. He already knows; He waits for you to tell Him.",
            },
            {
                heading: "Listen",
                body: "Prayer is not only speaking. Leave room for silence. In that silence, God speaks — not always in words, but in impressions, memories, a quiet certainty, a lifting of some weight you did not know you were carrying.",
            },
            {
                heading: "Use the words of the Church",
                body: "The Our Father, the Hail Mary, the Glory Be, the Rosary, the Divine Office — these are prayers polished by the saints for two millennia. When your own words fail, borrow theirs. They fit the human heart perfectly because they were shaped by grace.",
            },
            {
                heading: "Return often",
                body: "A single hour spent in prayer is beautiful. A minute spent in prayer many times a day, every day, becomes a life. Small, constant, honest — that is how the saints prayed.",
            },
            {
                heading: "On praying in Latin",
                body: "Latin is the sacred language of the Roman Church. It unites the faithful across every continent and every century. If you are new to it, do not fear — pronounce it slowly, reverently, and let its rhythm carry you. Your ear will learn what your mind cannot yet translate.",
            },
        ],
        outro: "Begin with the Sign of the Cross. That is enough for today.",
    },
    es: {
        kicker: "Un Camino Sencillo",
        title: "Cómo Orar",
        lead: "No hay fórmula. Sólo está el giro del corazón. Pero he aquí algunas puertas silenciosas — prácticas que la Iglesia ha enseñado durante siglos.",
        sections: [
            {
                heading: "Empieza con silencio",
                body: "Antes de las palabras, viene la quietud. Siéntate en algún lugar tranquilo. Respira. Haz la Señal de la Cruz — despacio. Recuerda que estás en presencia del Dios vivo. Esto solo ya es oración.",
            },
            {
                heading: "Habla sencillamente",
                body: "No necesitas elocuencia. Habla como lo harías con un padre, un hermano, un amigo. Dile lo que hay en tu corazón — la alegría, el dolor, la vergüenza, la gratitud. Él ya lo sabe; espera que se lo digas.",
            },
            {
                heading: "Escucha",
                body: "La oración no es sólo hablar. Deja espacio para el silencio. En ese silencio, Dios habla — no siempre con palabras, sino con impresiones, recuerdos, una certeza silenciosa, un levantamiento de algún peso que no sabías que llevabas.",
            },
            {
                heading: "Usa las palabras de la Iglesia",
                body: "El Padre Nuestro, el Ave María, el Gloria, el Rosario, la Liturgia de las Horas — estas oraciones han sido pulidas por los santos durante dos milenios. Cuando tus propias palabras fallen, toma las de ellos. Se ajustan perfectamente al corazón humano porque fueron moldeadas por la gracia.",
            },
            {
                heading: "Vuelve a menudo",
                body: "Una hora dedicada a la oración es hermosa. Un minuto de oración muchas veces al día, todos los días, se convierte en una vida. Pequeño, constante, sincero — así oraron los santos.",
            },
            {
                heading: "Sobre orar en latín",
                body: "El latín es la lengua sagrada de la Iglesia Romana. Une a los fieles a través de cada continente y cada siglo. Si eres nuevo en él, no temas — pronúncialo lenta, reverentemente, y deja que su ritmo te lleve. Tu oído aprenderá lo que tu mente todavía no puede traducir.",
            },
        ],
        outro: "Empieza con la Señal de la Cruz. Eso es suficiente por hoy.",
    },
};

const HowToPray = () => {
    const [lang, setLang] = useState("en");
    const c = CONTENT[lang];

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="how-pray-page">
            <div className="flex justify-center gap-1 mb-10">
                {LANGS.map(l => (
                    <button key={l.code} onClick={() => setLang(l.code)} data-testid={`how-lang-${l.code}`}
                        className={`px-3 py-1.5 text-xs small-caps border ${lang===l.code ? 'border-liturgy accent-liturgy' : 'border-border text-muted-foreground hover:accent-liturgy'}`}>
                        {l.label}
                    </button>
                ))}
            </div>

            <div className="text-center mb-14">
                <div className="small-caps text-xs text-muted-foreground mb-3">{c.kicker}</div>
                <h1 className="font-serif text-4xl md:text-5xl leading-tight">{c.title}</h1>
                <p className="mt-6 max-w-xl mx-auto text-lg text-muted-foreground italic leading-relaxed">{c.lead}</p>
            </div>

            <div className="space-y-12">
                {c.sections.map((s, i) => (
                    <section key={i}>
                        <h2 className="font-serif text-2xl mb-3 accent-liturgy">{s.heading}</h2>
                        <p className="text-base leading-loose text-foreground/90">{s.body}</p>
                    </section>
                ))}
            </div>

            <div className="mt-16 text-center border-t border-border pt-10">
                <p className="italic text-muted-foreground leading-relaxed max-w-xl mx-auto">{c.outro}</p>
                <div className="mt-6 flex gap-3 justify-center flex-wrap">
                    <Link to="/why-we-pray" className="px-5 py-2 border border-liturgy accent-liturgy text-xs small-caps hover:bg-liturgy hover:text-white transition-colors" data-testid="link-why">
                        {lang === "es" ? "Por qué Oramos" : "Why We Pray"}
                    </Link>
                    <Link to="/what-to-pray" className="px-5 py-2 border border-liturgy accent-liturgy text-xs small-caps hover:bg-liturgy hover:text-white transition-colors" data-testid="link-what">
                        {lang === "es" ? "Qué Orar" : "What to Pray"}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default HowToPray;
